import { valid, type Declaration, type ListedDeclaration, type SettingsRegistry } from "idfweb-settings";

import type { FormGroup, FormItem, FormModel, FormNode } from "./settings-form.ts";

/**
 * The settings registry as a form model: a group per `group` path, top-level
 * groups in alphabetical order, and the settings in each group by `order`
 * and then by title.
 */
export function settingsModel(registry: SettingsRegistry): FormModel {
    const listeners = new Set<(ids: readonly string[] | undefined) => void>();
    const watched = new Map<string, () => void>();
    const emit = (ids: readonly string[] | undefined) => {
        for (const listener of listeners) listener(ids);
    };
    const watch = () => {
        for (const declaration of registry.declarations()) {
            if (watched.has(declaration.key)) continue;
            const off = registry.setting(declaration.key)!.onChange(() => emit([declaration.key]));
            watched.set(declaration.key, off);
        }
    };
    const handle = (id: string) => {
        const setting = registry.setting(id);
        if (setting === undefined) throw new Error(`no setting ${id}`);
        return setting;
    };

    return {
        tree: () => settingsTree(registry.declarations()),
        state: (id) => {
            const setting = handle(id);
            const value = setting.get();
            // The tree lists no list settings; see ListedDeclaration.
            if (typeof value === "object") throw new Error(`setting ${id} is a list`);
            return {
                value,
                visible: true,
                readOnly: setting.fixed ? "This deployment of idfweb sets this." : undefined,
                modified: setting.modified,
            };
        },
        set: (id, value) => {
            const setting = handle(id);
            if (!valid(setting.declaration, value)) throw new Error(refusal(setting.declaration));
            setting.set(value);
        },
        reset: (id) => handle(id).reset(),
        onChange: (listener) => {
            if (listeners.size === 0) watch();
            listeners.add(listener);
            const offDeclare = registry.onDeclare(() => {
                watch();
                listener(undefined);
            });
            return () => {
                listeners.delete(listener);
                offDeclare();
                if (listeners.size > 0) return;
                for (const off of watched.values()) off();
                watched.clear();
            };
        },
    };
}

export function settingsTree(declarations: readonly ListedDeclaration[]): FormNode[] {
    const root: FormGroup = { kind: "group", id: "", title: "", children: [] };
    for (const declaration of declarations) {
        let group = root;
        for (const title of declaration.group) {
            let next = group.children.find((node): node is FormGroup => node.kind === "group" && node.title === title);
            if (next === undefined) {
                next = { kind: "group", id: `${group.id}/${title}`, title, children: [] };
                group.children.push(next);
            }
            group = next;
        }
        group.children.push(item(declaration));
    }
    const order = new Map(declarations.map((d) => [d.key, d.order ?? 0]));
    const sort = (nodes: FormNode[]): FormNode[] =>
        nodes
            .map((node) => (node.kind === "group" ? { ...node, children: sort(node.children) } : node))
            .sort((a, b) => {
                if (a.kind !== b.kind) return a.kind === "item" ? -1 : 1;
                if (a.kind === "item") return order.get(a.id)! - order.get(b.id)! || a.title.localeCompare(b.title);
                return a.title.localeCompare(b.title);
            });
    return sort(root.children);
}

function item(declaration: ListedDeclaration): FormItem {
    const common = {
        kind: "item" as const,
        id: declaration.key,
        title: declaration.title,
        description: declaration.description,
    };
    switch (declaration.type) {
        case "enum":
            return { ...common, control: { type: "enum", options: declaration.options } };
        case "number":
            return {
                ...common,
                control: { type: "number", min: declaration.min, max: declaration.max, integer: declaration.integer },
            };
        default:
            return { ...common, control: { type: declaration.type } };
    }
}

function refusal(declaration: Declaration): string {
    if (declaration.type !== "number") return "This value is not allowed.";
    const kind = declaration.integer ? "a whole number" : "a number";
    if (declaration.min !== undefined && declaration.max !== undefined)
        return `Enter ${kind} from ${declaration.min} to ${declaration.max}.`;
    if (declaration.min !== undefined) return `Enter ${kind} of at least ${declaration.min}.`;
    if (declaration.max !== undefined) return `Enter ${kind} of at most ${declaration.max}.`;
    return `Enter ${kind}.`;
}
